import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import { DONATION_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/donationsColumnsConfig';
import { buildSalesFilterGroups } from '../constants/salesFiltersConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const DONATIONS_CONFIG = {
  pageTitle: 'Donations',
  pageSubtitle: 'Donation revenue across your venues.',
  sidebarKey: 'sales_donations',
  defaultPreset: 'this_month',

  columns: DONATION_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesDonationColumns_${teamId}`,
  firstColumnId: 'order_number',

  salesDataKey: 'donations',
  venueSeriesKey: 'donations',
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-donations/data.json`,
  detailResponseKey: 'donations',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-donations/export`,
  salesExportSection: 'donations',
  detailErrorMessage: 'Failed to load donation data.',
  detailTableTitle: 'Donation Orders',
  emptyMessage: 'No donations found.',
  searchPlaceholder: 'Search donations...',
  paginationLabel: 'donations',
  itemLabel: (count) => `donation${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    { label: 'Donation Revenue', value: compactCurrency(current.revenue || 0), current: current.revenue || 0, prior: prior.revenue || 0 },
    { label: 'Donations', value: (current.count || 0).toLocaleString(), current: current.count || 0, prior: prior.count || 0 },
  ],
  kpiGridColumns: 2,

  chartColor: '#FAB714',
  salesCardTitle: 'Donations',
  buildMetricValue: (d) => currencyFormatter.format(d.revenue || 0),
  buildMetricLabel: (d) => `${(d.count || 0).toLocaleString()} donations`,
  overTimeKey: 'over_time',
  countLabel: 'donations',

  topCharts: [
    { title: 'Top 10 Events by Donations', dataKey: 'top_events_by_donation_count', barDataKey: 'donations_count', barColor: '#FAB714', isCurrency: false, tooltipLabel: 'Donations' },
    { title: 'Top 10 Events by Donation Revenue', dataKey: 'top_events_by_donation_revenue', barDataKey: 'revenue', barColor: '#FAB714', isCurrency: true, tooltipLabel: 'Donation Revenue' },
  ],

  groupByOptions: [
    { key: 'event', label: 'Event' },
    { key: 'donation', label: 'Donation Order' },
  ],
  groupByDefault: 'donation',
  eventGroupedColumns: [
    { key: 'event_name', label: 'Event' },
    { key: 'venue_name', label: 'Venue' },
    { key: 'event_date', label: 'Event Date' },
    { key: 'order_count', label: 'Orders' },
    { key: 'donation_count', label: 'Donations' },
    { key: 'gross_revenue', label: 'Gross' },
    { key: 'refunds', label: 'Refunds' },
    { key: 'total_revenue', label: 'Net Revenue' },
  ],

  renderEventCell: (key, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (key) {
      case 'event_name': return row.event_name || '\u2014';
      case 'venue_name': return row.venue_name || '\u2014';
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'order_count': return row.order_count.toLocaleString();
      case 'donation_count': return row.donation_count.toLocaleString();
      case 'gross_revenue': return cf.format(row.gross_revenue || 0);
      case 'refunds': return cf.format(row.refunds || 0);
      case 'total_revenue': return cf.format(row.total_revenue || 0);
      default: return '\u2014';
    }
  },

  renderCellValue: (col, donation, { formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'order_number': return donation.order_number;
      case 'event_name': return donation.event_name || '\u2014';
      case 'venue_name': return donation.venue_name || '\u2014';
      case 'customer_name': return donation.customer_name || '\u2014';
      case 'customer_email': return donation.customer_email || '\u2014';
      case 'donation_amount': return cf.format(donation.donation_amount || 0);
      case 'order_date': return <span className="text-nowrap">{formatEventDateTime(donation.order_date)}</span>;
      case 'sold_by': return donation.sold_by || '\u2014';
      case 'device_name': return donation.device_name || '\u2014';
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Donations',
  filterSubtitle: 'Apply filters to narrow donation data.',
  buildFilterGroups: (venueOwnerships, eventOptions, tagOptions, sellerOptions) =>
    buildSalesFilterGroups(venueOwnerships, eventOptions, tagOptions, { sellerOptions }),
};

function ReportingSalesDonationsPage(props) {
  return <SalesDetailPage {...props} config={DONATIONS_CONFIG} />;
}

export default ReportingSalesDonationsPage;
